/**
 * One funnel, both pixels. Every shop conversion event goes through here so
 * TikTok and Meta always receive the SAME event at the SAME moment with the
 * SAME identity — content_id values match the server-side conversion calls
 * (String(_id), see utils/marketing/promotable.ts) and the purchase event id
 * is the deterministic `order:<orderId>` shared with the Stripe webhook, so
 * the platforms dedupe browser vs server automatically.
 *
 * Consent is handled underneath: both pixel plugins drop calls until the
 * `marketing` category is granted, so call sites fire unconditionally.
 */
export function useShopTracking() {
    const { $tiktokPixel, $metaPixel } = useNuxtApp() as any

    interface Item { id: string; name?: string; priceEur?: number; quantity?: number }

    const num = (v: any) => (Number.isFinite(Number(v)) ? Number(v) : undefined)

    function both(
        tiktokEvent: string,
        metaEvent: string,
        items: Item[],
        valueEur?: number,
        eventId?: string,
    ) {
        const ids = items.map(i => String(i.id)).filter(Boolean)
        const name = items[0]?.name
        const value = num(valueEur)
        try {
            $tiktokPixel?.track(tiktokEvent, {
                content_type: 'product',
                content_id: ids[0],
                contents: ids.map(id => ({ content_id: id })),
                ...(name ? { content_name: name } : {}),
                ...(value !== undefined ? { value, currency: 'EUR' } : {}),
            }, eventId)
        } catch {}
        try {
            $metaPixel?.track(metaEvent, {
                content_type: 'product',
                content_ids: ids,
                ...(name ? { content_name: name } : {}),
                ...(value !== undefined ? { value, currency: 'EUR' } : {}),
            }, eventId)
        } catch {}
    }

    return {
        /** Buyer landed on a product/release/merch page. */
        viewContent(item: Item) {
            both('ViewContent', 'ViewContent', [item], item.priceEur)
        },
        /** Buyer added an item to the cart. */
        addToCart(item: Item) {
            both('AddToCart', 'AddToCart', [item], item.priceEur)
        },
        /** Buyer clicked through to the payment session. */
        initiateCheckout(items: Item[], totalEur?: number) {
            both('InitiateCheckout', 'InitiateCheckout', items, totalEur)
        },
        /**
         * Browser twin of the webhook's server-side conversion. Fire once per
         * order (the success page can be revisited for 72h — guard with the
         * localStorage marker) with eventId `order:<orderId>` for dedup.
         */
        purchase(orderId: string, items: Item[], totalEur?: number) {
            const marker = `dz_purchase_fired:${orderId}`
            try { if (localStorage.getItem(marker)) return } catch {}
            both('CompletePayment', 'Purchase', items, totalEur, `order:${orderId}`)
            try { localStorage.setItem(marker, '1') } catch {}
        },
    }
}
